import { createSlice } from '@reduxjs/toolkit';
import { getActiveProjectId } from 'uf/app/selectors';
import { Api } from 'uf-api-rtk/store/Api';
import { logEvent } from 'uf/logging';
import { LayerManagerState } from './state';

const initialState: LayerManagerState = {
  isOpen: false,
  selectedLayerId: null,
  searchString: '',
  isExpandedByCategory: {},
  staleSearches: [],
};

export const layerManagerSlice = createSlice({
  name: 'layerManager',
  initialState,
  reducers: {
    dialogClosed: state => {
      state.isOpen = false;
    },
    dialogOpened: state => {
      state.isOpen = true;
    },
    layerSelected: (state, action) => {
      state.selectedLayerId = action.payload;
    },
    searchUpdated: (state, action) => {
      state.searchString = action.payload;
    },
    categoryExpanded: (state, action) => {
      state.isExpandedByCategory[action.payload] = true;
    },
    categoryCollapsed: (state, action) => {
      state.isExpandedByCategory[action.payload] = false;
    },
    searchCleared: (state, action) => {
      state.staleSearches = [...state.staleSearches, action.payload];
    },
  },
  extraReducers(builder) {
    builder.addMatcher(
      Api.endpoints.addToLayerList.matchFulfilled,
      (state, action) => {
        // Clear stale searches if a layer is successfully added to the project
        state.staleSearches = [];
        const { key: projectId, layerPath: layerId } =
          action.meta.arg.originalArgs;
        logEvent('Layer Manager: Layer Added To Project', {
          projectId,
          layerId,
          searchTerm: state.searchString,
        });
      },
    );
    builder.addMatcher(
      Api.endpoints.removeFromLayerList.matchFulfilled,
      (state, action) => {
        const { key: projectId, layerPath: layerId } =
          action.meta.arg.originalArgs;
        logEvent('Layer Manager: Layer Removed From Project', {
          projectId,
          layerId,
          searchTerm: state.searchString,
        });
      },
    );
  },
});

export const {
  dialogClosed,
  dialogOpened,
  layerSelected,
  searchCleared,
  searchUpdated,
  categoryExpanded,
  categoryCollapsed,
} = layerManagerSlice.actions;

export const openLayerManager = () => (dispatch, getState) => {
  const projectId = getActiveProjectId(getState());
  logEvent('Layer Manager: Opened', { projectId });
  dispatch(dialogOpened());
};

export const closeLayerManager = () => (dispatch, getState) => {
  const projectId = getActiveProjectId(getState());
  const staleSearches = selectStaleSearches(getState());
  // Append stale search values if no layers are added during the manager session
  const eventProperties = staleSearches?.length
    ? { projectId, staleSearches }
    : { projectId };
  logEvent('Layer Manager: Closed', eventProperties);
  dispatch(dialogClosed());
};

export const selectLayer = layerId => (dispatch, getState) => {
  const projectId = getActiveProjectId(getState());
  const searchTerm = selectActiveSearchTerm(getState());
  logEvent('Layer Manager: Layer Viewed', { projectId, layerId, searchTerm });
  dispatch(layerSelected(layerId));
};

export const clearSearch = (searchTerm: string) => (dispatch, getState) => {
  const projectId = getActiveProjectId(getState());
  logEvent('Layer Manager: Search Cleared', { projectId, searchTerm });
  if (searchTerm.length) {
    dispatch(searchCleared(searchTerm));
  }
  dispatch(searchUpdated(''));
};

export const selectLayerManager = state => state.projects.layerManager;
const selectStaleSearches = state => state.projects.layerManager.staleSearches;
const selectActiveSearchTerm = state =>
  state.projects.layerManager.searchString;

export default layerManagerSlice.reducer;
